import { isRecord } from './isRecord';
import { isPaymentAccount } from './isPaymentAccount';
import { isOrderPayment } from './orderPaymentParsers';
import { unwrapListPayload } from './unwrapListPayload';
import { type IPaymentRefundList } from '@/types/IPaymentRefundList';
import { type IPaymentRefundRequest } from '@/types/IPaymentRefundRequest';

/**
 * Читает строковое поле объекта ответа.
 * @param source - объект ответа
 * @param key - имя поля
 * @returns строка либо null, если поле отсутствует или другого типа
 */
const readString = (
  source: Record<string, unknown>,
  key: string
): string | null => {
  const value = source[key];
  return typeof value === 'string' ? value : null;
};

/**
 * Читает числовое поле объекта ответа.
 * @param source - объект ответа
 * @param key - имя поля
 * @returns число либо null, если поле отсутствует или другого типа
 */
const readNumber = (
  source: Record<string, unknown>,
  key: string
): number | null => {
  const value = source[key];
  return typeof value === 'number' ? value : null;
};

/**
 * Читает идентификатор заявки.
 *
 * По контракту `id` — строка UUID: так он приходит в ответе создания, в
 * решениях и в реестре. Внутри `payments[].paymentRefundRequests` бэк временно
 * присылает пустой массив (дефект заведён) — до исправления такая заявка
 * остаётся без идентификатора и решение по ней принимается только в реестре.
 *
 * @param source - объект заявки из ответа
 * @returns UUID заявки либо null
 */
const readRefundUuid = (source: Record<string, unknown>): string | null =>
  readString(source, 'id');

/**
 * Достаёт заказ, по которому создан возврат.
 *
 * Заказ лежит во вложенном `payment.order_id`: несмотря на имя, бэк кладёт
 * туда объект заказа целиком, а не идентификатор. У заявок, вложенных в платёж
 * заказа, платежа нет — там заказ недоступен и не нужен.
 *
 * @param source - объект заявки из ответа
 * @returns объект заказа либо null
 */
const readOrder = (
  source: Record<string, unknown>
): Record<string, unknown> | null => {
  const { payment } = source;
  if (!isRecord(payment)) return null;
  return isRecord(payment.order_id) ? payment.order_id : null;
};

/**
 * Разбирает заявку на возврат ДС из ответа бэка.
 * @param value - элемент ответа
 * @returns заявка либо null, если значение не объект
 */
export const parsePaymentRefundRequest = (
  value: unknown
): IPaymentRefundRequest | null => {
  if (!isRecord(value)) return null;

  const order = readOrder(value);

  return {
    uuid: readRefundUuid(value),
    amount: readNumber(value, 'amount'),
    status: readString(value, 'status'),
    comment: readString(value, 'comment'),
    createdAt: readString(value, 'created_at'),
    updatedAt: readString(value, 'updated_at'),
    payment: isOrderPayment(value.payment) ? value.payment : null,
    orderUuid: order === null ? null : readString(order, 'uuid'),
    orderFormatedId: order === null ? null : readString(order, 'formated_id'),
    orderedBy: isPaymentAccount(value.ordered_by) ? value.ordered_by : null,
    approvedBy: isPaymentAccount(value.approved_by) ? value.approved_by : null
  };
};

/**
 * Разбирает ответ одной заявкой: создание, принятие и отклонение возвращают
 * саму заявку в конверте `{ response: … }`.
 *
 * @param payload - тело ответа
 * @returns заявка либо null, если тело не разбирается
 */
export const parsePaymentRefundResponse = (
  payload: unknown
): IPaymentRefundRequest | null => {
  if (!isRecord(payload)) return null;
  return parsePaymentRefundRequest(
    isRecord(payload.response) ? payload.response : payload
  );
};

/**
 * Разбирает список заявок из произвольного значения.
 * @param value - предполагаемый массив заявок
 * @returns разобранные заявки, элементы-не-объекты отбрасываются
 */
const parseItems = (value: unknown): IPaymentRefundRequest[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(parsePaymentRefundRequest)
    .filter((item): item is IPaymentRefundRequest => item !== null);
};

/**
 * Разбирает ответ GET /admin/payment-refunds.
 * @param payload - тело ответа
 * @returns список заявок и общее количество
 */
export const parsePaymentRefundList = (
  payload: unknown
): IPaymentRefundList => {
  const { items, total } = unwrapListPayload(payload);
  return { items: parseItems(items), total };
};

/**
 * Достаёт заявки на возврат из платежа заказа.
 *
 * Поле `paymentRefundRequests` приходит в ответе GET /admin/payments/{uuid},
 * но не в ответах создания платежа и смены его статуса, поэтому проверяется
 * наличием ключа, а не типом аргумента.
 *
 * @param payment - платёж заказа
 * @returns разобранные заявки платежа
 */
export const getPaymentRefundRequests = (
  payment: unknown
): IPaymentRefundRequest[] => {
  if (!isRecord(payment)) return [];
  return parseItems(payment.paymentRefundRequests);
};
